import {CdFrequency} from '../../../interfaces/care/cd-care-plan/cd-frequency';
import {CdFrequencyItem} from '../../../interfaces/care/cd-care-plan/cd-frequency-item';
import {CdFrequencyTagFactory} from './cd-frequency-tag-factory';
import {CdFrequencyItemFactory} from './cd-frequency-item-factory';
import {CdFrequencyTag} from '../../../interfaces/care/cd-care-plan/cd-frequency-tag';
import {CdFrequencyIntervalFactory} from './cd-frequency-interval-factory';
import {CdFrequencyInterval} from '../../../interfaces/care/cd-care-plan/cd-frequency-interval';
import {CdFrequencyPeriod} from '../../../interfaces/care/cd-care-plan/cd-frequency-period';
import {CdFrequencyPeriodFactory} from './cd-frequency-period-factory';
import {Service} from '../../../interfaces/core/service';
import {ServiceFactory} from '../../core/service-factory';


export class CdFrequencyFactory {

  /**
   *
   * @param cdFrequency
   */
  public static fromObject(cdFrequency: any): CdFrequency {
    return new class implements CdFrequency {
      cdCarePlanId: number = cdFrequency.c_care_plan_id;
      cdFrequencyTypeId: number = cdFrequency.c_frequency_type_id;
      parentId: number = cdFrequency.pivot?.c_care_plan_item_id;
      comment: string = cdFrequency.comment;
      created: string = cdFrequency.created;
      id: number = cdFrequency.id;
      interval: number = cdFrequency.interval;
      modified: string = cdFrequency.modified;
      service: Service = cdFrequency.service ? ServiceFactory.fromObject(cdFrequency.service) : null;
      serviceId: number = cdFrequency.service_id;
      userId: number = cdFrequency.user_id;
      weekday: number = cdFrequency.weekday;
      cdFrequencyItems: CdFrequencyItem[] =
        cdFrequency.c_care_plan_items ?
          CdFrequencyItemFactory.fromArray(cdFrequency.c_care_plan_items) : [];
      cdFrequencyTag: CdFrequencyTag = cdFrequency.tag ? CdFrequencyTagFactory.fromObject(cdFrequency.tag) : null;
      cdFrequencyIntervals: CdFrequencyInterval[] =
        cdFrequency.c_frequency_intervals ? CdFrequencyIntervalFactory.fromArray(cdFrequency.c_frequency_intervals) : [];
      cdFrequencyPeriods: CdFrequencyPeriod[] = cdFrequency.c_frequency_periods ?
        CdFrequencyPeriodFactory.fromArray(cdFrequency.c_frequency_periods)  : [];
    };
  }

  /**
   *
   * @param cdFrequencies
   */
  public static fromArray(cdFrequencies: any[]): CdFrequency[] {
    return cdFrequencies.map(_cdFrequency => CdFrequencyFactory.fromObject(_cdFrequency));
  }

  /**
   * Maps a CdFrequency to its remote payload. Only fields that we
   * actually create locally (see new()) and that the backend is
   * supposed to receive are included. The following are explicitly
   * omitted:
   *  - userId / user_id (set server-side)
   *  - parentId (handled via the pivot relation server-side)
   *  - service (denormalized embedding, redundant with serviceId)
   *  - cdFrequencyTag (computed/denormalized server-side)
   *  - cdFrequencyItems (managed via the parent CdCarePlanItem)
   *
   * IMPORTANT: Frequencies are only sent to the server in the
   * context of a CdTemporaryMeasure (see
   * CdTemporaryMeasureFactory.toRemote). Frequencies attached to a
   * regular CdCarePlanItem are NOT editable from this client and
   * must NOT be serialized through this method anywhere else.
   *
   * @param cdFrequency
   */
  public static toRemote(cdFrequency: CdFrequency): any {
    return {
      // id: cdFrequency.id,
      c_frequency_type_id: cdFrequency.cdFrequencyTypeId,
      service_id: cdFrequency.serviceId,
      interval: cdFrequency.interval,
      comment: cdFrequency.comment,
      created: cdFrequency.created,
      modified: cdFrequency.modified,
      c_frequency_intervals: CdFrequencyIntervalFactory.toArray(cdFrequency.cdFrequencyIntervals ?? []),
      c_frequency_periods: CdFrequencyPeriodFactory.toArray(cdFrequency.cdFrequencyPeriods ?? [])
    };
  }

  /**
   * See toRemote() - same restriction applies: only call from the
   * context of a CdTemporaryMeasure.
   *
   * @param cdFrequencies
   */
  public static toArray(cdFrequencies: CdFrequency[]): any[] {
    return cdFrequencies.map(_cdFrequency => CdFrequencyFactory.toRemote(_cdFrequency));
  }

  /**
   *
   */
  public static new(): CdFrequency {
    return new class implements CdFrequency {
      id: number = null;
      cdCarePlanId: number = null;
      cdFrequencyTypeId: number = null;
      serviceId: number = null;
      interval: number = 1;
      comment: string = null;
      userId: number = null;
      created: string = new Date().toISOString();
      modified: string = new Date().toISOString();
      cdFrequencyItems: CdFrequencyItem[] = [];
      cdFrequencyTag: CdFrequencyTag = null;
      parentId: number = null;
      cdFrequencyIntervals: CdFrequencyInterval[] = [];
      cdFrequencyPeriods: CdFrequencyPeriod[] = [];
      service: Service = null;
    };
  }

}
